// src/pages/ProviderSignUpPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { signupSuccessPath } from '../utils/googleAuth';
import PasswordInput from '../components/PasswordInput';
import GoogleSignInButton, { GoogleAccountNote } from '../components/GoogleSignInButton';
import useGoogleAuthNavigation from '../hooks/useGoogleAuthNavigation';
import BrandName from '../components/BrandName';
import SpamFolderWarning from '../components/SpamFolderWarning';
import RegisterCheck from '../components/RegisterCheck';
import Seo from '../components/Seo';
import { SIMPLE_PAGES } from '../data/simplePages';
import { webPageJsonLd } from '../data/pageSchema';
import './AuthPages.css';

const seo = SIMPLE_PAGES.find((page) => page.path === '/provider-signup');

const ProviderSignUpPage = () => {
  const { signup, error, signInWithGoogle, completeGoogleSignup, pendingSetup, logout } = useAuth();
  const navigate                  = useNavigate();
  useGoogleAuthNavigation('signup');
  const [form, setForm] = useState(() => ({
    name: pendingSetup?.name || '',
    businessName: '',
    email: pendingSetup?.email || '',
    password: '',
    location: '',
    vehicle: '',
    licenceNo: '',
  }));
  const [agreed, setAgreed]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = e => setForm({...form, [e.target.name]: e.target.value});

  useEffect(() => {
    if (!pendingSetup) return;
    setForm((prev) => ({
      ...prev,
      email: pendingSetup.email || prev.email,
      name: prev.name || pendingSetup.name || '',
    }));
  }, [pendingSetup]);

  const carrierExtra = () => ({
    location:                       form.location,
    vehicle:                        form.vehicle,
    licenceNo:                      form.licenceNo,
    verified:                       false, // Admin must verify licence before they can bid
    carrierAgreementAccepted:       true,
    carrierAgreementAcceptedAt:     serverTimestamp(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;
    setLoading(true);
    const displayName = form.businessName || form.name;
    const result = pendingSetup
      ? await completeGoogleSignup('provider', displayName, carrierExtra())
      : await signup(form.email, form.password, 'provider', displayName, carrierExtra());
    if (result.success) {
      // If the verification email failed to send, go straight to /verify-email,
      // which explains the problem and offers Resend. A verified Google account
      // goes to the dashboard. Licence approval is unchanged (verified stays false).
      const next = signupSuccessPath(result);
      if (next) navigate(next, { replace: true });
      else setSubmitted(true);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    await signInWithGoogle();
    setGoogleLoading(false);
  };

  useEffect(() => {
    if (!submitted) return;
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/verify-email');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [submitted, navigate]);

  if (submitted) return (
    <div className="auth-page">
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={webPageJsonLd(seo)} />
      <div className="auth-card" style={{ textAlign: 'center' }}>
        <div className="auth-logo"><BrandName /></div>
        <SpamFolderWarning />
        <p className="auth-sub" style={{ marginTop: '4px' }}>
          Continuing to inbox instructions in <strong>{countdown}s</strong>…
        </p>
        <button
          className="btn btn-primary btn-full"
          style={{ marginTop: '12px' }}
          onClick={() => navigate('/verify-email')}
        >
          Continue Now
        </button>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <Seo title={seo.title} description={seo.description} path={seo.path} jsonLd={webPageJsonLd(seo)} />
      <div className="auth-card">
        <div className="auth-logo"><BrandName /></div>
        <h2>Become a Carrier</h2>
        <p className="auth-sub">Start bidding on jobs and earning Eco Credits</p>
        {error && <div className="auth-error">{error}</div>}
        <div className="auth-hint">
          🛡️ You will need your <strong>waste carrier registration number</strong> to complete registration.{' '}
          <RegisterCheck />
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Your Name *</label>
              <input className="form-input" name="name" placeholder="Full name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Business Name</label>
              <input className="form-input" name="businessName" placeholder="Trading as..." value={form.businessName} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Waste carrier licence number *</label>
            <input className="form-input" name="licenceNo" placeholder="Registration number" value={form.licenceNo} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="form-input" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} required readOnly={!!pendingSetup} />
          </div>
          {!pendingSetup && (
            <div className="form-group">
              <label className="form-label">Password *</label>
              <PasswordInput className="form-input" name="password" placeholder="Min 6 characters" value={form.password} onChange={handleChange} required autoComplete="new-password" />
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" name="location" placeholder="e.g. Swansea" value={form.location} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Vehicle Type</label>
              <select className="form-select" name="vehicle" value={form.vehicle} onChange={handleChange}>
                <option value="">Select...</option>
                <option>Small Van</option>
                <option>Large Van</option>
                <option>Pickup Truck</option>
                <option>Luton Van</option>
                <option>Skip Lorry</option>
              </select>
            </div>
          </div>
          <div className="auth-agreement-check">
            <label className="auth-agreement-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
              />
              <span>
                I have read and agree to the{' '}
                <a className="auth-agreement-link" href="/carrier-agreement" target="_blank" rel="noopener noreferrer">Carrier Agreement</a>,{' '}
                <a className="auth-agreement-link" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>, and{' '}
                <a className="auth-agreement-link" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </span>
            </label>
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading || googleLoading || !agreed}>
            {loading ? 'CREATING ACCOUNT...' : 'CREATE CARRIER ACCOUNT'}
          </button>
          {pendingSetup ? (
            <GoogleAccountNote email={pendingSetup.email} onUseDifferent={logout} />
          ) : (
            <GoogleSignInButton
              label="Sign up with Google"
              onClick={handleGoogle}
              disabled={loading}
              busy={googleLoading}
            />
          )}
        </form>
        <p className="auth-footer">Already have an account? <Link to="/login">Log in</Link></p>
      </div>
    </div>
  );
};

export default ProviderSignUpPage;
